import { supabase } from "./supabase";
import { CardData, UpdateCardDTO, CardStatus, CreateCardDTO } from "../types/card.types";
import { generateId } from "../utils/helpers";
import { calculatePetals } from "../utils/petals.utils";
import { lrnsyCryptoService } from "./lrnsy-crypto.service";
import { privacyLogger } from "./privacy-logger.service";
import { Database, Json } from "../types/supabase.types";
import { ultraPrivateKeyVaultService } from "./ultra-private-key-vault.service";

type CardInsert = Database["public"]["Tables"]["cards"]["Insert"];
type CardUpdate = Database["public"]["Tables"]["cards"]["Update"];
type CardAccessEventInsert = Database["public"]["Tables"]["card_access_events"]["Insert"];
type PrivacyModeDb = Database["public"]["Enums"]["lrnsy_privacy_mode"];
type AccessStatusDb = Database["public"]["Enums"]["lrnsy_access_status"];

type CardRow = Database["public"]["Tables"]["cards"]["Row"];

class CardService {
  private readonly TABLE = "cards";
  private cache: Map<string, { data: CardData; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000;

  private handleError(error: any, message: string): never {
    privacyLogger.error(message, error);
    throw new Error(`${message}: ${error?.message || "Unknown error"}`);
  }

  private assertActiveCard(row: CardRow) {
    if (row.access_status === "deleted") throw new Error("This experience has been deleted");
    if (row.access_status === "revoked") throw new Error("This private link has been revoked");
    if (row.access_status === "expired") throw new Error("This experience has expired");

    if (row.expires_at && new Date(row.expires_at) < new Date()) {
      throw new Error("This experience has expired");
    }
  }

  async createCard(params: CreateCardDTO): Promise<CardData> {
    try {
      const now = new Date().toISOString();
      const expiresAt =
        params.customization.expiresAt ||
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

      if (!params.senderId) throw new Error("User ID is required to create a card");

      const privacyMode = params.customization.privacyMode || "standard_private";
      const encrypted = await lrnsyCryptoService.encryptCardContent({
        message: params.customization.message || "",
        personalNote: params.customization.personalNote,
        memory: params.customization.memory,
        insideJoke: params.customization.insideJoke,
        privacyMode,
        senderId: params.senderId,
      });

      const passcodeResult =
        params.customization.requiresPasscode && params.customization.passcode
          ? await lrnsyCryptoService.hashPasscode(params.customization.passcode)
          : null;

      const petals = calculatePetals(params.customization.bouquetAmount);
      const shareableLink = await this.generateUniqueLink();

      const newCard: CardInsert = {
        id: generateId(),
        shareable_link: shareableLink,
        sender_id: params.senderId,
        sender_email: params.senderEmail,
        sender_name: params.senderName,
        recipient_name: params.customization.recipientName || "",
        recipient_type: params.customization.recipientType || "partner",
        recipient_image_url: params.customization.recipientImage || null,
        theme: params.customization.theme || "valentine",

        /**
         * NEVER store Tier 3 emotional content here.
         * Keep empty for legacy compatibility only.
         */
        message: "",

        title: params.customization.title || "To My Love",
        primary_color: params.customization.primaryColor || "#ff6b6b",
        secondary_color: params.customization.secondaryColor || "#feca57",
        accent_color: params.customization.accentColor || null,
        font_family: params.customization.fontFamily || "Inter",
        include_bouquet: params.customization.includeBouquet || false,
        bouquet_amount: params.customization.bouquetAmount || 0,
        status: "draft" as CardStatus,
        payment_ref: null,
        created_at: now,
        updated_at: now,
        expires_at: expiresAt,
        viewed_at: null,
        accepted_at: null,
        petals,
        is_editable: true,

        privacy_mode: privacyMode as PrivacyModeDb,
        access_status: "active" as AccessStatusDb,
        encrypted_message: JSON.stringify(encrypted.encryptedMessage),
        encrypted_personal_note: encrypted.encryptedPersonalNote
          ? JSON.stringify(encrypted.encryptedPersonalNote)
          : null,
        encrypted_memory: encrypted.encryptedMemory
          ? JSON.stringify(encrypted.encryptedMemory)
          : null,
        encrypted_inside_joke: encrypted.encryptedInsideJoke
          ? JSON.stringify(encrypted.encryptedInsideJoke)
          : null,
        encryption_version: "lrnsy-webcrypto-aes-gcm-v1",
        encryption_salt: encrypted.encryptedMessage.salt,
        encryption_iv: encrypted.encryptedMessage.iv,
        encrypted_key: encrypted.encryptedKey || null,
        key_wrap_version: privacyMode === "standard_private" ? "client-wrapped-v1" : null,
        requires_passcode: Boolean(params.customization.requiresPasscode),
        passcode_hash: passcodeResult?.hash || null,
        passcode_salt: passcodeResult?.salt || null,
        delete_after_open: Boolean(params.customization.deleteAfterOpen),
        privacy_metadata: {
          privacy_mode: privacyMode,
          has_passcode: Boolean(params.customization.requiresPasscode),
          delete_after_open: Boolean(params.customization.deleteAfterOpen),
          created_from: "web_app",
        } as Json,
      };

      const { data, error } = await supabase.from(this.TABLE).insert(newCard).select().single();
      if (error) this.handleError(error, "Failed to create card");

      const mapped = this.mapToCardData(data);

      if (privacyMode === "ultra_private" && encrypted.fragmentKey) {
        ultraPrivateKeyVaultService.saveKey(mapped.id, encrypted.fragmentKey);

        mapped.shareableLink = lrnsyCryptoService.buildUltraPrivateLink(
          mapped.shareableLink,
          encrypted.fragmentKey
        );
      }

      await this.auditCardEvent(mapped.id, "card_created", params.senderId, {
        privacyMode,
        hasPasscode: Boolean(params.customization.requiresPasscode),
      });

      return mapped;
    } catch (error) {
      this.handleError(error, "Failed to create card");
    }
  }

  async getCard(id: string): Promise<CardData> {
    try {
      const cached = this.cache.get(id);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) return cached.data;

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error || !data) throw new Error("Card not found");

      this.assertActiveCard(data as CardRow);

      const cardData = this.mapToCardData(data);
      this.cache.set(id, { data: cardData, timestamp: Date.now() });
      return cardData;
    } catch (error) {
      this.handleError(error, "Failed to get card");
    }
  }

  async getCardByLinkId(linkId: string): Promise<CardData> {
    try {
      const cleanLinkId = linkId.split("#")[0];

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("shareable_link", cleanLinkId)
        .maybeSingle();

      if (error || !data) throw new Error("Link not found or expired");

      this.assertActiveCard(data as CardRow);

      await this.registerOpen(data.id);

      return this.mapToCardData(data);
    } catch (error) {
      this.handleError(error, "Failed to get card by link");
    }
  }

  async updateCard(id: string, updates: UpdateCardDTO): Promise<CardData> {
    try {
      const patch: CardUpdate = {
        updated_at: new Date().toISOString(),
      };

      if (updates.status) patch.status = updates.status;
      if (typeof updates.is_editable === "boolean") patch.is_editable = updates.is_editable;
      if (typeof updates.petals === "number") patch.petals = updates.petals;

      if (updates.customization) {
        const customization = updates.customization;
        const privacyMode = customization.privacyMode || "standard_private";

        if (
          typeof customization.message === "string" ||
          typeof customization.personalNote === "string" ||
          typeof customization.memory === "string" ||
          typeof customization.insideJoke === "string"
        ) {
          const encrypted = await lrnsyCryptoService.encryptCardContent({
            message: customization.message || "",
            personalNote: customization.personalNote,
            memory: customization.memory,
            insideJoke: customization.insideJoke,
            privacyMode,
            senderId: "",
          });

          patch.message = "";
          patch.privacy_mode = privacyMode as PrivacyModeDb;
          patch.encrypted_message = JSON.stringify(encrypted.encryptedMessage);
          patch.encrypted_personal_note = encrypted.encryptedPersonalNote
            ? JSON.stringify(encrypted.encryptedPersonalNote)
            : null;
          patch.encrypted_memory = encrypted.encryptedMemory
            ? JSON.stringify(encrypted.encryptedMemory)
            : null;
          patch.encrypted_inside_joke = encrypted.encryptedInsideJoke
            ? JSON.stringify(encrypted.encryptedInsideJoke)
            : null;
          patch.encrypted_key = encrypted.encryptedKey || null;
          patch.key_wrap_version = privacyMode === "standard_private" ? "client-wrapped-v1" : null;
        }

        if (customization.recipientName !== undefined)
          patch.recipient_name = customization.recipientName;
        if (customization.senderName !== undefined) patch.sender_name = customization.senderName;
        if (customization.recipientImage !== undefined) {
          patch.recipient_image_url = customization.recipientImage;
        }
        if (customization.title !== undefined) patch.title = customization.title;
        if (customization.theme !== undefined) patch.theme = customization.theme;
        if (customization.primaryColor !== undefined)
          patch.primary_color = customization.primaryColor;
        if (customization.secondaryColor !== undefined)
          patch.secondary_color = customization.secondaryColor;
        if (customization.accentColor !== undefined) patch.accent_color = customization.accentColor;
        if (customization.fontFamily !== undefined) patch.font_family = customization.fontFamily;
        if (customization.includeBouquet !== undefined) {
          patch.include_bouquet = customization.includeBouquet;
        }
        if (customization.bouquetAmount !== undefined) {
          patch.bouquet_amount = customization.bouquetAmount;
        }
        if (customization.deleteAfterOpen !== undefined) {
          patch.delete_after_open = customization.deleteAfterOpen;
        }
      }

      const { data, error } = await supabase
        .from(this.TABLE)
        .update(patch)
        .eq("id", id)
        .select()
        .single();

      if (error) this.handleError(error, "Failed to update card");

      const mapped = this.mapToCardData(data);
      this.cache.set(id, { data: mapped, timestamp: Date.now() });
      return mapped;
    } catch (error) {
      this.handleError(error, "Failed to update card");
    }
  }

  async markCardAsPaid(cardId: string, paymentRef = "free_access"): Promise<boolean> {
    try {
      const { error } = await supabase
        .from("cards")
        .update({
          status: "paid",
          payment_ref: paymentRef,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cardId);

      if (error) {
        privacyLogger.error("Error marking card as paid", error);
        return false;
      }

      await this.auditCardEvent(cardId, "card_paid", null, { paymentRef });
      return true;
    } catch (error) {
      privacyLogger.error("Error marking card as paid", error);
      return false;
    }
  }

  async markAsReceived(cardId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE)
        .update({
          status: "received",
          accepted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", cardId);

      if (error) this.handleError(error, "Failed to mark card as received");

      await this.auditCardEvent(cardId, "card_received", null, {});
      this.cache.delete(cardId);
    } catch (error) {
      this.handleError(error, "Failed to mark card as received");
    }
  }

  async revokeLink(cardId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from(this.TABLE)
      .update({
        access_status: "revoked",
        revoked_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", cardId)
      .eq("sender_id", userId);

    if (error) this.handleError(error, "Failed to revoke private link");

    await this.auditCardEvent(cardId, "link_revoked", userId, {});
    this.cache.delete(cardId);
    return true;
  }

  async deleteExperience(cardId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from(this.TABLE)
      .update({
        access_status: "deleted",
        deleted_at: new Date().toISOString(),
        encrypted_message: null,
        encrypted_personal_note: null,
        encrypted_memory: null,
        encrypted_inside_joke: null,
        encrypted_key: null,
        message: "",
        recipient_image_url: null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", cardId)
      .eq("sender_id", userId);

    if (error) this.handleError(error, "Failed to delete experience");

    await this.auditCardEvent(cardId, "experience_deleted", userId, {});
    this.cache.delete(cardId);
    return true;
  }

  async verifyPasscode(cardId: string, passcode: string): Promise<boolean> {
    const { data, error } = await supabase
      .from(this.TABLE)
      .select("passcode_hash, passcode_salt")
      .eq("id", cardId)
      .maybeSingle();

    if (error || !data?.passcode_hash || !data.passcode_salt) return false;

    const result = await lrnsyCryptoService.hashPasscode(passcode, data.passcode_salt);
    return result.hash === data.passcode_hash;
  }

  async markAsOpened(cardId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE)
        .update({
          viewed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } satisfies CardUpdate)
        .eq("id", cardId);

      if (error) this.handleError(error, "Failed to mark card as opened");

      await this.auditCardEvent(cardId, "card_opened", null, {});
      this.cache.delete(cardId);
    } catch (error) {
      this.handleError(error, "Failed to mark card as opened");
    }
  }

  async getUserCardsPaginated(
    userId: string,
    page: number,
    pageSize: number
  ): Promise<{ data: CardData[]; hasMore: boolean }> {
    try {
      const from = page * pageSize;
      const to = from + pageSize;

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("sender_id", userId)
        .neq("access_status", "deleted")
        .order("created_at", { ascending: false })
        .range(from, to);

      if (error) this.handleError(error, "Failed to get user cards");

      const rows = (data || []) as CardRow[];
      const mapped = rows.slice(0, pageSize).map((row) => this.mapToCardData(row));

      return {
        data: mapped,
        hasMore: rows.length > pageSize,
      };
    } catch (error) {
      this.handleError(error, "Failed to get user cards");
    }
  }

  private async registerOpen(cardId: string): Promise<void> {
    const { data } = await supabase
      .from(this.TABLE)
      .select("opened_count, delete_after_open")
      .eq("id", cardId)
      .maybeSingle();

    const openedCount = Number(data?.opened_count || 0) + 1;

    await supabase
      .from(this.TABLE)
      .update({
        opened_count: openedCount,
        last_opened_at: new Date().toISOString(),
        access_status: data?.delete_after_open ? "deleted" : "active",
        deleted_at: data?.delete_after_open ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", cardId);

    await this.auditCardEvent(cardId, "recipient_opened", null, {
      deleteAfterOpen: Boolean(data?.delete_after_open),
    });
  }

  private async auditCardEvent(
    cardId: string,
    eventType: string,
    actorUserId: string | null,
    metadata: Record<string, unknown>
  ) {
    try {
      const payload: CardAccessEventInsert = {
        card_id: cardId,
        event_type: eventType,
        actor_user_id: actorUserId,
        actor_role: actorUserId ? "sender" : "recipient_or_system",
        metadata: metadata as Json,
        created_at: new Date().toISOString(),
      };

      await supabase.from("card_access_events").insert(payload);
    } catch (error) {
      privacyLogger.warn("Failed to write card audit event", {
        cardId,
        eventType,
        error,
      });
    }
  }

  private async generateUniqueLink(): Promise<string> {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const link = lrnsyCryptoService.createOpaqueToken();

      const { data, error } = await supabase
        .from(this.TABLE)
        .select("id")
        .eq("shareable_link", link)
        .maybeSingle();

      if (!error && !data) return link;
    }

    throw new Error("Failed to generate private link");
  }

  private mapToCardData(row: CardRow): CardData {
    return {
      id: row.id,
      shareableLink: row.shareable_link,
      senderId: row.sender_id,
      senderEmail: row.sender_email,
      senderName: row.sender_name,
      recipientName: row.recipient_name,
      recipientType: row.recipient_type,
      recipientImage: row.recipient_image_url,
      theme: row.theme || "valentine",

      /**
       * Decrypted message is intentionally empty for persisted encrypted cards.
       * Preview cards and decrypted UI state can still supply plaintext locally.
       */
      message: row.message || "",

      title: row.title || "To You",
      primaryColor: row.primary_color || "#ff6b6b",
      secondaryColor: row.secondary_color || "#feca57",
      accentColor: row.accent_color || undefined,
      fontFamily: row.font_family || "Inter",
      includeBouquet: Boolean(row.include_bouquet),
      bouquetAmount: row.bouquet_amount || 0,
      status: row.status,
      paymentRef: row.payment_ref,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      expiresAt: row.expires_at,
      viewedAt: row.viewed_at,
      acceptedAt: row.accepted_at,
      petals: row.petals || 0,
      isEditable: Boolean(row.is_editable),

      includeHeartParticles: false,
      includeSparkles: false,

      archetype: row.archetype as CardData["archetype"],

      privacyMode: row.privacy_mode,
      accessStatus: row.access_status,
      encryptedMessage: row.encrypted_message,
      encryptedPersonalNote: row.encrypted_personal_note,
      encryptedMemory: row.encrypted_memory,
      encryptedInsideJoke: row.encrypted_inside_joke,
      encryptionVersion: row.encryption_version,
      encryptionSalt: row.encryption_salt,
      encryptionIv: row.encryption_iv,
      encryptedKey: row.encrypted_key,
      keyWrapVersion: row.key_wrap_version,
      requiresPasscode: row.requires_passcode,
      deleteAfterOpen: row.delete_after_open,
      openedCount: row.opened_count,
      lastOpenedAt: row.last_opened_at,
    };
  }
}

export const cardService = new CardService();
